import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle, ArrowRight, Phone, Check, Sun, Moon } from 'lucide-react';

const Register = () => {
  const { register } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await register(email, password, companyName);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || t('registerError'));
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    language === 'fr' ? '30 jours d\'essai gratuit' : '30 days free trial',
    language === 'fr' ? 'Sans carte bancaire' : 'No credit card required',
    language === 'fr' ? 'Annulation à tout moment' : 'Cancel anytime'
  ];

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0a0a0f]' : 'bg-gray-50'} flex`} style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Background effects */}
      {isDark && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#3272F1]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-600/5 rounded-full blur-3xl"></div>
        </div>
      )}

      {/* Left side - Benefits */}
      <div className="hidden lg:flex lg:w-1/2 relative z-10 flex-col justify-center p-12">
        <Link to="/" className="absolute top-6 left-6">
          <img 
            src="https://customer-assets.emergentagent.com/job_exitcall/artifacts/v6rf7bvn_logo.svg" 
            alt="HICALL" 
            className="h-10"
          />
        </Link>
        
        <div className="max-w-md">
          <h1 className={`text-4xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-6`}>
            {language === 'fr' 
              ? 'Commencez à convertir vos visiteurs dès aujourd\'hui'
              : 'Start converting your visitors today'}
          </h1>
          <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-lg mb-8`}>
            {language === 'fr'
              ? 'Rejoignez les entreprises qui ne laissent plus partir leurs prospects.'
              : 'Join companies that no longer let their prospects leave.'}
          </p>
          
          <div className="space-y-4">
            {benefits.map((benefit, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-6 h-6 bg-[#3272F1]/20 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-[#3272F1]" />
                </div>
                <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 relative z-10 flex flex-col">
        {/* Mobile header */}
        <header className="lg:hidden p-6 flex justify-between items-center">
          <Link to="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_exitcall/artifacts/v6rf7bvn_logo.svg" 
              alt="HICALL" 
              className="h-10"
            />
          </Link>
          <div className="flex items-center gap-2">
            <button 
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'}`}
              data-testid="theme-toggle-mobile"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button 
              onClick={toggleLanguage}
              className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} text-sm transition-colors`}
              data-testid="lang-toggle"
            >
              {language.toUpperCase()}
            </button>
          </div>
        </header>

        {/* Desktop header */}
        <div className="hidden lg:flex justify-end p-6 gap-2">
          <button 
            onClick={toggleTheme}
            className={`p-2 rounded-lg transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'}`}
            data-testid="theme-toggle-desktop"
          >
            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
          <button 
            onClick={toggleLanguage}
            className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} text-sm transition-colors px-2`}
            data-testid="lang-toggle-desktop"
          >
            {language.toUpperCase()}
          </button>
        </div>

        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            {/* Card */}
            <div className={`${isDark ? 'bg-gradient-to-br from-gray-900 to-gray-950 border-gray-800' : 'bg-white border-gray-200 shadow-lg'} rounded-2xl border p-8`}>
              <div className="text-center mb-8">
                <div className="w-14 h-14 bg-[#3272F1] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-7 h-7 text-white" />
                </div>
                <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
                  {t('registerTitle')}
                </h2>
                <p className={isDark ? 'text-gray-500' : 'text-gray-600'}>
                  {t('hasAccount')}{' '}
                  <Link to="/login" className="text-[#3272F1] hover:underline">
                    {t('signIn')}
                  </Link>
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg text-sm" data-testid="register-error">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="companyName" className={isDark ? 'text-gray-300' : 'text-gray-700'}>{t('companyName')}</Label>
                  <Input
                    id="companyName"
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Mon Entreprise"
                    required
                    className={`${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} focus:border-[#3272F1] focus:ring-[#3272F1]`}
                    data-testid="company-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className={isDark ? 'text-gray-300' : 'text-gray-700'}>{t('email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="vous@exemple.com"
                    required
                    className={`${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} focus:border-[#3272F1] focus:ring-[#3272F1]`}
                    data-testid="email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className={isDark ? 'text-gray-300' : 'text-gray-700'}>{t('password')}</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    minLength={6}
                    required
                    className={`${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} focus:border-[#3272F1] focus:ring-[#3272F1]`}
                    data-testid="password-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-[#3272F1] hover:bg-[#2560d9] text-white py-6 rounded-xl font-semibold" 
                  disabled={loading}
                  data-testid="register-submit"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      {t('signUp')}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Footer text */}
            <p className={`text-center ${isDark ? 'text-gray-600' : 'text-gray-500'} text-sm mt-6`}>
              {language === 'fr' 
                ? 'En créant un compte, vous acceptez nos conditions d\'utilisation et notre politique de confidentialité.'
                : 'By creating an account, you agree to our terms of service and privacy policy.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
